/**
 * Mock data for the Nimbus × Solomon YaaS "Create Program" flow.
 *
 * Persona: **Nimbus** — a consumer crypto neobank / DeFi savings app that
 * integrates Solomon YaaS to stream yield onto its users' USDv balances.
 * Programs here are Nimbus's user-acquisition & retention incentives
 * (Welcome Bonus, Savings Tier Boost, Direct Deposit Boost, Referral
 * Rewards, Loyalty Holding Rewards), each rewarding Nimbus *users*.
 *
 * All amounts are USDv. Reward rates are stored in basis points
 * (1% = 100 bps) so slider math stays integer-clean; descriptions format
 * back to percentages.
 *
 * NOTE: the program `id` slugs are intentionally left stable — the
 * Policies, Users, agents/holders, and funding mocks reference them by id.
 * Only the user-facing fields (name, subtitle, region, amounts, target)
 * describe the Nimbus world.
 */

export type ProgramStatus = "in_progress" | "active" | "ended";

export type ProgramTab = "all" | "active" | "ended" | "draft";

/** A reward rule driven by a single numeric value (rendered as an input). */
export interface SliderPolicy {
    kind: "slider";
    id: string;
    title: string;
    /** Renders the live description from the current numeric value. */
    describe: (value: number) => string;
    min: number;
    max: number;
    step: number;
    defaultValue: number;
    /** Optional denomination shown inside the input. `"usdpt"` renders the
     *  USDv logo + label suffix (instead of the min–max range hint). The
     *  literal stays `"usdpt"` to match the existing render switch. */
    unit?: "usdpt";
}

/** A reward rule with a fixed set of choices, driven by a select. */
export interface SelectPolicy {
    kind: "select";
    id: string;
    title: string;
    description: string;
    options: { value: string; label: string }[];
    defaultValue: string;
}

/** A wallet-list rule — configured by pasting one or more wallet
 *  addresses, plus a mode toggle (Whitelist / Blacklist / KYC Only).
 *  The value is JSON: `{ mode, addresses }`. In `kyc-only` mode the manual
 *  address list is unused (eligibility is gated on KYC-approved status). */
export interface WalletPolicy {
    kind: "wallet";
    id: string;
    title: string;
    description: string;
    /** Input placeholder, e.g. "Paste a wallet address". */
    placeholder: string;
    /** Mode toggle options. Each carries its own description (shown when
     *  that mode is active). First entry is the default mode. */
    modes?: { value: string; label: string; description: string }[];
}

/** A tranche rule — the user builds N tranches, each a threshold amount
 *  (USDv balance or days held) paired with a reward multiplier (e.g. 2 =
 *  2× = +100% boost). The value is JSON: an array of `{ amount, multiplier }`. */
export interface TranchePolicy {
    kind: "tranche";
    id: string;
    title: string;
    description: string;
    /** Unit of each tranche's threshold amount. */
    amountUnit: "usdpt" | "days";
    /** Seeded tranches (shown on live programs / as a starting point). */
    defaultTranches?: { amount: number; multiplier: number }[];
}

/** A purpose-built signup-bonus rule — a one-time USDv bonus paid to customers
 *  who joined within a recency window. Two number fields: `bonusAmount` (USDv,
 *  the one-time payout per user) and `joinedWithinDays` (the eligibility window
 *  in days). The value is JSON: `{ bonusAmount, joinedWithinDays }`.
 *  Consume-once semantics (permanently ineligible after payout) are BACKEND-
 *  enforced (stateful, per-user), not a UI control — see the neobank GAPS. */
export interface NewUserPolicy {
    kind: "new-user";
    id: string;
    title: string;
    description: string;
    /** Default bonus payout (USDv) for a freshly-chosen policy. */
    defaultBonusAmount: number;
    /** Default eligibility window (days) for a freshly-chosen policy. */
    defaultJoinedWithinDays: number;
}

/** A read-only policy added from a Suggested card — its config is
 *  summarized into a single description line (no inline control). */
export interface SummaryPolicy {
    kind: "summary";
    id: string;
    title: string;
    description: string;
}

export type Policy = SliderPolicy | SelectPolicy | WalletPolicy | TranchePolicy | NewUserPolicy | SummaryPolicy;

/** Policy templates that can be created & configured inline (everything
 *  except read-only summaries). */
export type ConfigurablePolicy = SliderPolicy | SelectPolicy | WalletPolicy | TranchePolicy | NewUserPolicy;

export interface ProgramRegion {
    value: string;
    label: string;
}

export interface ProgramPeriod {
    value: string;
    label: string;
    /** Days the period maps to — used in the budget helper copy. */
    days: number;
}

/** Next-distribution cycle for an active program (drives the
 *  countdown + accrued/projected meter). */
export interface NextDistribution {
    /** USDv accrued so far toward the next distribution. */
    accrued: number;
    /** USDv projected to be distributed at the next distribution. */
    projected: number;
    /** Length of the distribution cycle, in days (the meter denominator). */
    cycleDays: number;
    /** Seconds remaining until the next distribution (live countdown). */
    inSeconds: number;
}

export interface Program {
    id: string;
    name: string;
    subtitle: string;
    status: ProgramStatus;
    statusLabel: string;
    region: string;
    /** Who the program rewards. In the Nimbus world every program targets
     *  the app's *users*; the underlying union stays `"agents" | "holders"`
     *  to match the shared shape, and the UI renders it as "Users". */
    target: "agents" | "holders";
    period: string;
    /** Duration model. `fixed` runs for `period`; `continuous` runs open-ended
     *  at a fixed `baseApy`. Defaults to fixed. */
    programType?: "fixed" | "continuous";
    /** For fixed-length programs: `fixed` caps the rewards `budget`; `open`
     *  runs the period with no budget cap. Defaults to fixed. */
    budgetMode?: "fixed" | "open";
    /** Base APY (%) distributed, for continuous programs. */
    baseApy?: number;
    /** Reward share (%) allocated to program participants. */
    rewardsPct?: number;
    /** Variable (earned) budget only: the % of earnings passed to the user
     *  (the remainder is kept by the client). Shown as the "User %" field.
     *  Defaults to `rewardsPct` when absent. */
    userPct?: number;
    /** Exact duration in days when known from required configuration policy. */
    periodDays?: number;
    /** Settlement cycle cadence from the required configuration policy. */
    settlementCycleLength?: number;
    settlementCycleUnit?: "days" | "weeks";
    policies: Policy[];
    budget: number;
    /** Days elapsed in the program period so far (active programs only). */
    daysElapsed?: number;
    /** Next-distribution cycle (active programs only). */
    nextDistribution?: NextDistribution;
    /** Per-policy configured values overriding the policy-template defaults
     *  for this program (keyed by policy id). */
    policyValues?: Record<string, number | string>;
}

export const REGION_OPTIONS: ProgramRegion[] = [
    { value: "global", label: "Global" },
    { value: "united-states", label: "United States" },
    { value: "europe", label: "Europe" },
    { value: "latam", label: "Latin America" },
    { value: "apac", label: "Asia Pacific" },
    { value: "philippines", label: "Philippines" },
    { value: "bolivia", label: "Bolivia" }
];

export const PERIOD_OPTIONS: ProgramPeriod[] = [
    { value: "1m", label: "1m", days: 30 },
    { value: "3m", label: "3m", days: 90 },
    { value: "6m", label: "6m", days: 180 },
    { value: "12m", label: "12m", days: 365 }
];

/**
 * Canonical policy-type templates. Both the seeded programs and the
 * "+Create Policy" catalog are assembled from these constants, so the
 * builder and the programs never drift. All reward logic keys off the
 * user's USDv *holding* (balance size, time held, wallet status).
 */
const MINIMUM_THRESHOLD: SliderPolicy = {
    kind: "slider",
    id: "minimum-threshold",
    title: "Minimum Threshold",
    describe: () => "Require a minimum balance to qualify.",
    min: 0,
    max: 10000,
    step: 100,
    defaultValue: 1000,
    unit: "usdpt"
};

const TIME_HELD_TRANCHE: TranchePolicy = {
    kind: "tranche",
    id: "time-held-tranche",
    title: "Time Held Tranche",
    description: "Create tranches for different amount of time held.",
    amountUnit: "days",
    defaultTranches: [
        { amount: 0, multiplier: 1 },
        { amount: 30, multiplier: 1.25 },
        { amount: 90, multiplier: 1.5 }
    ]
};

const BALANCE_AMOUNT_TRANCHE: TranchePolicy = {
    kind: "tranche",
    id: "balance-amount-tranche",
    title: "Balance Amount Tranche",
    description: "Create tranches for different balance amounts.",
    amountUnit: "usdpt",
    defaultTranches: [
        { amount: 0, multiplier: 1 },
        { amount: 10000, multiplier: 1.5 },
        { amount: 50000, multiplier: 2 }
    ]
};

const WALLET_LIST: WalletPolicy = {
    kind: "wallet",
    id: "wallet-list",
    title: "Specific Access Control",
    description: "Restrict eligibility by wallet address",
    placeholder: "Paste a wallet address",
    modes: [
        {
            value: "whitelist",
            label: "Whitelist",
            description: "Only allow whitelisted wallets to qualify."
        },
        {
            value: "blacklist",
            label: "Blacklist",
            description: "Prevent blacklisted wallets to qualify."
        },
        {
            value: "kyc-only",
            label: "KYC Only",
            description: "Only KYC-approved wallets qualify."
        }
    ]
};

const NEW_USER: NewUserPolicy = {
    kind: "new-user",
    id: "new-user",
    title: "New User",
    description: "One-time bonus for customers who recently joined.",
    defaultBonusAmount: 25,
    defaultJoinedWithinDays: 30
};

/**
 * Shared boost-tranche presets reused across programs. Serialized to the
 * same JSON shape `serializeTranches` produces (`{amount, multiplier}[]`),
 * so they slot straight into a program's `policyValues`.
 */
const BALANCE_BOOST_TRANCHES = JSON.stringify([
    { amount: 10000, multiplier: 1.1 },
    { amount: 100000, multiplier: 1.2 }
]);
const TIME_HELD_BOOST_TRANCHES = JSON.stringify([
    { amount: 30, multiplier: 1.1 },
    { amount: 60, multiplier: 1.2 },
    { amount: 90, multiplier: 1.3 },
    { amount: 120, multiplier: 1.4 },
    { amount: 150, multiplier: 1.5 }
]);

// Draft starts with no policies so the "+Create Policy" dropdown lists all
// available types — the program builder demo starts from a clean slate.
export const SETTLEMENT_ACCELERATOR_POLICIES: Policy[] = [];

/** Welcome Bonus — a one-time USDv bonus for new users who fund a wallet.
 *  Still a draft (not yet launched), so the builder starts from a clean slate. */
export const WELCOME_BONUS: Program = {
    id: "agent-usdpt-settlement-accelerator",
    name: "Welcome Bonus",
    subtitle: "New incentive program — not yet launched",
    status: "in_progress",
    statusLabel: "Draft",
    region: "global",
    target: "agents",
    period: "3m",
    policies: SETTLEMENT_ACCELERATOR_POLICIES,
    budget: 250000
};

/** Savings Tier Boost — live retention program that pays a higher APY as a
 *  user's USDv balance and holding time grow. */
export const SAVINGS_TIER_BOOST: Program = {
    id: "bolivia-agent-wallet-activation-drive",
    name: "Savings Tier Boost",
    subtitle: "Live incentive program",
    status: "active",
    statusLabel: "Active",
    region: "global",
    target: "agents",
    period: "6m",
    programType: "fixed",
    budgetMode: "open",
    baseApy: 6.5,
    policies: [TIME_HELD_TRANCHE, MINIMUM_THRESHOLD, BALANCE_AMOUNT_TRANCHE],
    budget: 900000,
    daysElapsed: 64,
    // Balance + time-held boost tranches (shared preset).
    policyValues: {
        "balance-amount-tranche": BALANCE_BOOST_TRANCHES,
        "time-held-tranche": TIME_HELD_BOOST_TRANCHES
    },
    nextDistribution: {
        accrued: 21000,
        projected: 35000,
        cycleDays: 7,
        inSeconds: 223200
    }
};

/** Direct Deposit Boost — continuous program rewarding users who route a
 *  recurring deposit into their USDv balance. */
export const DIRECT_DEPOSIT_BOOST: Program = {
    id: "us-usdpt-settlement-rebate",
    name: "Direct Deposit Boost",
    subtitle: "Live incentive program",
    status: "active",
    statusLabel: "Active",
    region: "united-states",
    target: "holders",
    period: "12m",
    programType: "continuous",
    baseApy: 6.0,
    policies: [BALANCE_AMOUNT_TRANCHE, MINIMUM_THRESHOLD, TIME_HELD_TRANCHE],
    budget: 1200000,
    daysElapsed: 120,
    nextDistribution: {
        accrued: 14000,
        projected: 23000,
        cycleDays: 7,
        inSeconds: 376800
    }
};

/** Referral Rewards — completed acquisition program that paid a USDv bonus
 *  when a referred user activated and funded a wallet. */
export const REFERRAL_REWARDS: Program = {
    id: "bolivia-q1-remittance-rewards",
    name: "Referral Rewards",
    subtitle: "Completed incentive program",
    status: "ended",
    statusLabel: "Ended",
    region: "global",
    target: "agents",
    period: "3m",
    policies: [MINIMUM_THRESHOLD],
    // Budget == rewards distributed so an ended program reads 100% exhausted.
    budget: 137088,
    daysElapsed: 90
    // No nextDistribution — the program has ended.
};

/** Loyalty Holding Rewards — live program that boosts yield the longer a
 *  user holds a qualifying USDv balance. */
export const LOYALTY_HOLDING_REWARDS: Program = {
    id: "us-revolut-partnership-program",
    name: "Loyalty Holding Rewards",
    subtitle: "Live incentive program",
    status: "active",
    statusLabel: "Active",
    region: "global",
    target: "holders",
    period: "6m",
    policies: [MINIMUM_THRESHOLD, BALANCE_AMOUNT_TRANCHE, TIME_HELD_TRANCHE],
    budget: 600000,
    daysElapsed: 0,
    // Minimum balance 100 USDv, plus balance + time-held boost tranches.
    policyValues: {
        "minimum-threshold": 100,
        "balance-amount-tranche": BALANCE_BOOST_TRANCHES,
        "time-held-tranche": TIME_HELD_BOOST_TRANCHES
    },
    nextDistribution: {
        accrued: 9000,
        projected: 18000,
        cycleDays: 7,
        inSeconds: 259200
    }
};

export const PROGRAMS: Program[] = [
    WELCOME_BONUS,
    SAVINGS_TIER_BOOST,
    DIRECT_DEPOSIT_BOOST,
    REFERRAL_REWARDS,
    LOYALTY_HOLDING_REWARDS
];

/**
 * Cross-domain relationship helpers — single source of truth for the
 * Programs ↔ Policies and Programs ↔ Agents links surfaced in the
 * master-detail detail panes. Derived from the seeded PROGRAMS so the
 * Policies and Agents pages never duplicate the linkage.
 */

/** Programs whose policy set includes the given policy id. */
export function programsUsingPolicy(policyId: string): Program[] {
    return PROGRAMS.filter((program) => program.policies.some((policy) => policy.id === policyId));
}

/** Resolve program ids → programs, in PROGRAMS order. */
export function programsByIds(ids: string[]): Program[] {
    return PROGRAMS.filter((p) => ids.includes(p.id));
}

/** Mock enrolled-user counts per program — powers the blast-radius preview
 *  shown before a policy value change is confirmed. Keyed by program id. */
const PROGRAM_USER_COUNTS: Record<string, number> = {
    "bolivia-agent-wallet-activation-drive": 3420,
    "us-usdpt-settlement-rebate": 1880,
    "us-revolut-partnership-program": 5240,
    "bolivia-q1-remittance-rewards": 940,
    "agent-usdpt-settlement-accelerator": 1260
};

/** Enrolled-user count for a program (0 if unknown). */
export function programUserCount(programId: string): number {
    return PROGRAM_USER_COUNTS[programId] ?? 0;
}

/** Policy ids attached to a given program. */
export function policiesForProgram(program: Program): string[] {
    return program.policies.map((policy) => policy.id);
}

/**
 * Catalog of policy templates the program builder's "+ Create Policy"
 * inline editor can pick from and configure. A program can attach any
 * of these that it doesn't already have. Canonical defaults; once
 * attached, a policy is read-only in the program view (its values are
 * edited only in the Policies section).
 */
export const POLICY_CATALOG: ConfigurablePolicy[] = [
    MINIMUM_THRESHOLD,
    TIME_HELD_TRANCHE,
    BALANCE_AMOUNT_TRANCHE,
    WALLET_LIST,
    NEW_USER
];
